// =============================================================================
// zipStore — minimalny writer ZIP w trybie STORE (bez kompresji).
//
// Po co wlasny: w package.json nie ma zadnej biblioteki ZIP, a paczka faviconow
// to same PNG-i (juz skompresowane) + kilka malych plikow tekstowych — deflate
// nic by tu nie ugral. Zero nowych zaleznosci, czysta funkcja, testowalna.
//
// Format: [local header + dane] * N + [central directory] * N + EOCD.
// =============================================================================

export interface ZipEntry {
  /** Nazwa pliku w archiwum (ASCII — bez polskich znakow). */
  name: string;
  bytes: Uint8Array;
}

const LOCAL_HEADER_SIG = 0x04034b50;
const CENTRAL_HEADER_SIG = 0x02014b50;
const EOCD_SIG = 0x06054b50;
const VERSION = 20; // 2.0 — wystarcza dla STORE

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

export function crc32(data: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < data.length; i++) c = CRC_TABLE[(c ^ data[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/** Godzina w formacie MS-DOS (sekundy z dokladnoscia do 2s). */
function dosTime(d: Date): number {
  return (d.getHours() << 11) | (d.getMinutes() << 5) | (d.getSeconds() >> 1);
}

/** Data w formacie MS-DOS (rok liczony od 1980). */
function dosDate(d: Date): number {
  return ((d.getFullYear() - 1980) << 9) | ((d.getMonth() + 1) << 5) | d.getDate();
}

function asciiBytes(s: string): Uint8Array {
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i) & 0xff;
  return out;
}

/**
 * Buduje archiwum ZIP (STORE) z listy plikow.
 * `date` jest parametrem, zeby testy mogly liczyc na deterministyczne bajty.
 */
export function createZip(entries: ZipEntry[], date: Date = new Date()): Uint8Array {
  const time = dosTime(date);
  const day = dosDate(date);

  const prepared = entries.map((e) => ({
    nameBytes: asciiBytes(e.name),
    bytes: e.bytes,
    crc: crc32(e.bytes),
  }));

  const localSize = prepared.reduce((s, e) => s + 30 + e.nameBytes.length + e.bytes.length, 0);
  const centralSize = prepared.reduce((s, e) => s + 46 + e.nameBytes.length, 0);
  const buf = new Uint8Array(localSize + centralSize + 22);
  const view = new DataView(buf.buffer);

  // --- Local file headers + dane ---
  const offsets: number[] = [];
  let pos = 0;
  for (const e of prepared) {
    offsets.push(pos);
    view.setUint32(pos, LOCAL_HEADER_SIG, true);
    view.setUint16(pos + 4, VERSION, true);
    view.setUint16(pos + 6, 0, true); // flags
    view.setUint16(pos + 8, 0, true); // compression: 0 = store
    view.setUint16(pos + 10, time, true);
    view.setUint16(pos + 12, day, true);
    view.setUint32(pos + 14, e.crc, true);
    view.setUint32(pos + 18, e.bytes.length, true); // compressed
    view.setUint32(pos + 22, e.bytes.length, true); // uncompressed
    view.setUint16(pos + 26, e.nameBytes.length, true);
    view.setUint16(pos + 28, 0, true); // extra field length
    pos += 30;
    buf.set(e.nameBytes, pos);
    pos += e.nameBytes.length;
    buf.set(e.bytes, pos);
    pos += e.bytes.length;
  }

  // --- Central directory ---
  const centralStart = pos;
  prepared.forEach((e, i) => {
    view.setUint32(pos, CENTRAL_HEADER_SIG, true);
    view.setUint16(pos + 4, VERSION, true); // version made by
    view.setUint16(pos + 6, VERSION, true); // version needed
    view.setUint16(pos + 8, 0, true); // flags
    view.setUint16(pos + 10, 0, true); // compression
    view.setUint16(pos + 12, time, true);
    view.setUint16(pos + 14, day, true);
    view.setUint32(pos + 16, e.crc, true);
    view.setUint32(pos + 20, e.bytes.length, true);
    view.setUint32(pos + 24, e.bytes.length, true);
    view.setUint16(pos + 28, e.nameBytes.length, true);
    view.setUint16(pos + 30, 0, true); // extra
    view.setUint16(pos + 32, 0, true); // comment
    view.setUint16(pos + 34, 0, true); // disk number start
    view.setUint16(pos + 36, 0, true); // internal attrs
    view.setUint32(pos + 38, 0, true); // external attrs
    view.setUint32(pos + 42, offsets[i], true);
    pos += 46;
    buf.set(e.nameBytes, pos);
    pos += e.nameBytes.length;
  });

  // --- End of central directory ---
  view.setUint32(pos, EOCD_SIG, true);
  view.setUint16(pos + 4, 0, true); // numer dysku
  view.setUint16(pos + 6, 0, true); // dysk z central directory
  view.setUint16(pos + 8, prepared.length, true);
  view.setUint16(pos + 10, prepared.length, true);
  view.setUint32(pos + 12, centralSize, true);
  view.setUint32(pos + 16, centralStart, true);
  view.setUint16(pos + 20, 0, true); // dlugosc komentarza

  return buf;
}
